import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface PricingCardProps {
  name: string;
  description: string;
  price: string;
  priceSubtext: string;
  features: string[];
  highlighted?: boolean;
  buttonText: string;
  buttonVariant?: 'default' | 'outline';
  onSelect: () => void;
  loading?: boolean;
  disabled?: boolean;
  badge?: string;
}

export function PricingCard({
  name,
  description,
  price,
  priceSubtext,
  features,
  highlighted = false,
  buttonText,
  buttonVariant = 'default',
  onSelect,
  loading = false,
  disabled = false,
  badge,
}: PricingCardProps) {
  return (
    <Card
      className={cn(
        'relative flex flex-col',
        highlighted && 'border-primary shadow-lg ring-1 ring-primary/20'
      )}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
            {badge}
          </span>
        </div>
      )}
      <CardHeader className="pb-4">
        <CardTitle className="text-xl">{name}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <div className="mb-6">
          <span className="text-4xl font-bold">{price}</span>
          <span className="text-muted-foreground ml-1 text-sm">{priceSubtext}</span>
        </div>

        <ul className="space-y-3 mb-8 flex-1">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          variant={buttonVariant}
          className="w-full"
          onClick={onSelect}
          disabled={loading || disabled}
        >
          {loading ? 'Processing...' : buttonText}
        </Button>
      </CardContent>
    </Card>
  );
}
